/**
 * Kary porządkowe — art. 108–113 k.p. (Etap 38, ADR-0039).
 *
 * Karta w zakładce „Zatrudnienie" akt pracownika. Kara to POSTĘPOWANIE, nie
 * wpis kwoty: formularz pyta o naruszenie, dzień powzięcia wiadomości
 * i wysłuchanie, bo od nich zależą terminy z art. 109 § 1. Kwotę kary
 * pieniężnej ogranicza jednodniowe wynagrodzenie (art. 108 § 3) — serwer
 * odmawia z nazwanym powodem, a nie ogólnym błędem.
 *
 * Sprzeciw wnosi pracownik z portalu (art. 112 § 1). Tutaj kadry go rozpatrują
 * i mogą uznać karę za niebyłą; wtedy odpis zawiadomienia znika z akt.
 * Uznanie za niebyłą jest nieodwracalne, więc wymaga podstawy z art. 113 § 1
 * — formularz pokazuje tylko te, które pasują do stanu sprawy, i mówi, czy
 * potrącona kwota wróci (art. 112 § 3; fala 8, grupa 52).
 */
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Card,
  EmptyState,
  FormField,
  InfoBanner,
  Input,
  Modal,
  Select,
  Spinner,
  StatusPill,
  useToast,
  type StatusPillTone,
} from '@k1p4/ui'
import { formatDate, formatPLN } from '@k1p4/i18n'
import {
  PENALTY_KINDS,
  PENALTY_OFFENCES,
  PENALTY_VOID_BASES,
  voidBasisIssue,
  voidBasisRefunds,
  type PenaltyKind,
  type PenaltyVoidBasis,
} from '@k1p4/shared'
import { ApiError, api, apiDay, type PenaltyItem } from '../../api/client'
import styles from './FamilyMembers.module.css'

export interface PenaltiesProps {
  employeeId: string
  /** Wymierzanie kary i decyzje wymagają uprawnienia „Kadry : Edycja". */
  canEdit: boolean
  /**
   * Czy karta dotyczy rodzaju umowy osoby (fala 8, grupa 87; D98). Przy
   * „nie dotyczy" karta pokazuje tylko istniejące wpisy, do odczytu, a bez
   * wpisów nie renderuje się wcale.
   */
  applicable?: boolean
}

const STATUS_TONE: Record<string, StatusPillTone> = {
  zastosowana: 'warning',
  sprzeciw: 'pink',
  sprzeciw_odrzucony: 'warning',
  sprzeciw_uwzgledniony: 'success',
  niebyla: 'neutral',
}

interface Form {
  kind: PenaltyKind | ''
  offence: string
  description: string
  offenceOn: string
  learnedOn: string
  hearingOn: string
  appliedOn: string
  amount: string
}

const EMPTY: Form = {
  kind: '',
  offence: '',
  description: '',
  offenceOn: '',
  learnedOn: '',
  hearingOn: '',
  appliedOn: '',
  amount: '',
}

/** Złote z formularza na grosze; puste pole to brak wartości, nie zero. */
function grosze(value: string): number | undefined {
  const normalized = value.replace(/\s/g, '').replace(',', '.')
  if (normalized === '') return undefined
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : undefined
}

export function Penalties({ employeeId, canEdit: canEditProp, applicable = true }: PenaltiesProps) {
  const canEdit = canEditProp && applicable
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const ids = {
    kind: useId(),
    offence: useId(),
    description: useId(),
    offenceOn: useId(),
    learnedOn: useId(),
    hearingOn: useId(),
    appliedOn: useId(),
    amount: useId(),
    notifiedOn: useId(),
    comment: useId(),
    voidBasis: useId(),
    voidReason: useId(),
  }

  const [addOpen, setAddOpen] = useState(false)
  const [form, setForm] = useState<Form>(EMPTY)
  const [notify, setNotify] = useState<PenaltyItem | null>(null)
  const [notifiedOn, setNotifiedOn] = useState('')
  const [decide, setDecide] = useState<PenaltyItem | null>(null)
  const [comment, setComment] = useState('')
  const [voidTarget, setVoidTarget] = useState<PenaltyItem | null>(null)
  const [voidBasis, setVoidBasis] = useState<PenaltyVoidBasis | ''>('')
  const [voidReason, setVoidReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [issues, setIssues] = useState<readonly string[]>([])

  const penalties = useQuery({
    queryKey: ['penalties', employeeId],
    queryFn: () => api.penalties(employeeId),
  })

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['penalties', employeeId] })
    /* Zawiadomienie i zatarcie zmieniają akta i listę dokumentów. */
    void queryClient.invalidateQueries({ queryKey: ['documents'] })
  }

  const add = useMutation({
    mutationFn: () =>
      api.addPenalty(employeeId, {
        kind: form.kind as PenaltyKind,
        offence: form.offence,
        description: form.description.trim(),
        offenceOn: form.offenceOn,
        learnedOn: form.learnedOn,
        ...(form.hearingOn ? { hearingOn: form.hearingOn } : {}),
        appliedOn: form.appliedOn,
        ...(grosze(form.amount) !== undefined ? { amountGr: grosze(form.amount)! } : {}),
      }),
    onSuccess: (result) => {
      toast.show(t('penalties.applied', { date: formatDate(result.voidOn, 'dot') }), {
        tone: 'success',
      })
      setAddOpen(false)
      setForm(EMPTY)
      setError(null)
      setIssues([])
      refresh()
    },
    onError: (caught) => {
      if (caught instanceof ApiError && caught.code === 'PENALTY_ISSUES') {
        setIssues(caught.issues)
        setError('penalties.errorIssues')
        return
      }
      setError(
        caught instanceof ApiError && caught.code === 'NOT_AN_EMPLOYEE'
          ? 'penalties.errorNotEmployee'
          : caught instanceof ApiError && caught.code === 'NO_SALARY'
          ? 'penalties.errorNoSalary'
          : caught instanceof ApiError && caught.code === 'AMOUNT_REQUIRED'
            ? 'penalties.errorAmount'
            : 'penalties.errorGeneric',
      )
    },
  })

  const markNotified = useMutation({
    mutationFn: () => api.penaltyNotified(notify!.id, notifiedOn),
    onSuccess: (result) => {
      toast.show(t('penalties.notified', { date: formatDate(result.objectionDeadline, 'dot') }), {
        tone: 'success',
      })
      setNotify(null)
      setNotifiedOn('')
      setError(null)
      refresh()
    },
    onError: () => setError('penalties.errorGeneric'),
  })

  const decision = useMutation({
    mutationFn: (accepted: boolean) =>
      api.penaltyObjectionDecision(decide!.id, accepted, comment.trim() || undefined),
    onSuccess: (result) => {
      toast.show(t(`penalties.decision.${result.status}`), { tone: 'success' })
      /* Mail jest zawiadomieniem z art. 112 § 2 — bez konta trzeba doręczyć pismo. */
      if (!result.notified) toast.show(t('penalties.decisionNotMailed'), { tone: 'info' })
      setDecide(null)
      setComment('')
      setError(null)
      refresh()
    },
    onError: (caught) => {
      setError(
        caught instanceof ApiError && caught.code === 'SILENCE_ACCEPTED'
          ? 'penalties.errorSilence'
          : 'penalties.errorGeneric',
      )
    },
  })

  const voidPenalty = useMutation({
    mutationFn: () =>
      api.penaltyVoid(voidTarget!.id, {
        basis: voidBasis as PenaltyVoidBasis,
        ...(voidReason.trim() ? { reason: voidReason.trim() } : {}),
      }),
    onSuccess: () => {
      toast.show(t('penalties.voided'), { tone: 'success' })
      setVoidTarget(null)
      setError(null)
      refresh()
    },
    onError: (caught) => {
      setError(
        caught instanceof ApiError && caught.code === 'VOID_TOO_EARLY'
          ? 'penalties.errorVoidTooEarly'
          : caught instanceof ApiError && caught.code === 'BASIS_MISMATCH'
            ? 'penalties.errorBasisMismatch'
            : 'penalties.errorGeneric',
      )
    },
  })

  const today = apiDay()
  /* Podstawy, które pasują do stanu sprawy — ta sama reguła co na serwerze. */
  const voidBases =
    voidTarget === null || voidTarget.voidOn === null
      ? []
      : PENALTY_VOID_BASES.filter(
          (basis) =>
            voidBasisIssue(
              basis,
              { status: voidTarget.status, voidOn: voidTarget.voidOn! },
              today,
            ) === null,
        )
  /* Zwrot z art. 112 § 3 dotyczy tylko kary pieniężnej, z której coś potrącono. */
  const refundGr =
    voidTarget !== null && voidBasis !== '' && voidBasisRefunds(voidBasis)
      ? voidTarget.deductedGr - voidTarget.refundedGr
      : 0

  const items = penalties.data?.items ?? []
  const ready =
    form.kind !== '' &&
    form.offence !== '' &&
    form.description.trim().length >= 3 &&
    form.offenceOn !== '' &&
    form.learnedOn !== '' &&
    form.appliedOn !== ''

  /* Karta „nie dotyczy” bez wpisów nie miga też w trakcie ładowania. */
  if (!applicable && items.length === 0) return null

  return (
    <section className={styles.section}>
      <div className={styles.head}>
        <h3 className={styles.title}>{t('penalties.title')}</h3>
        {canEdit ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setError(null)
              setIssues([])
              setAddOpen(true)
            }}
          >
            {t('penalties.add')}
          </Button>
        ) : null}
      </div>
      <p className={styles.note}>{t('penalties.lead')}</p>

      {error !== null && !addOpen && notify === null && decide === null && voidTarget === null ? (
        <InfoBanner tone="danger" className={styles.banner}>
          {t(error)}
        </InfoBanner>
      ) : null}

      {penalties.isPending ? (
        <Spinner label={t('penalties.loading')} />
      ) : items.length === 0 ? (
        <EmptyState title={t('penalties.empty')} description={t('penalties.emptyBody')} />
      ) : (
        <Card pad="flush" className={styles.list}>
          {items.map((item) => (
            <div key={item.id} className={styles.row}>
              <span className={styles.rowName}>
                {t(`penalties.kinds.${item.kind}`)}
                {item.amountGr === null ? '' : ` · ${formatPLN(item.amountGr)}`}
              </span>
              <span className={styles.rowMeta}>
                {t(`penalties.offences.${item.offence}`)}
                {' · '}
                {t('penalties.offenceOn', { date: formatDate(item.offenceOn, 'dot') })}
                {item.deductedGr > 0
                  ? ` · ${t('penalties.deducted', { amount: formatPLN(item.deductedGr) })}`
                  : ''}
                {item.refundedGr > 0
                  ? ` · ${t('penalties.refunded', { amount: formatPLN(item.refundedGr) })}`
                  : ''}
              </span>
              <span className={styles.rowDates}>
                {item.notifiedOn === null
                  ? t('penalties.notNotified')
                  : t('penalties.objectionUntil', {
                      date: formatDate(item.objectionDeadline!, 'dot'),
                    })}
                {item.objectionDecidedBy === 'milczenie' ? ` · ${t('penalties.bySilence')}` : ''}
                {item.courtDeadline !== null && item.status === 'sprzeciw_odrzucony'
                  ? ` · ${t('penalties.courtUntil', { date: formatDate(item.courtDeadline, 'dot') })}`
                  : ''}
                {item.voidBasis !== null
                  ? ` · ${t(`penalties.voidBases.${item.voidBasis}`)}`
                  : ''}
                {/* Dzień zatarcia z art. 113 § 1 — o „nienaganności" nie orzekamy. */}
                {item.voidOn !== null && item.status !== 'niebyla'
                  ? ` · ${t('penalties.voidOn', { date: formatDate(item.voidOn, 'dot') })}`
                  : ''}
              </span>
              <StatusPill tone={STATUS_TONE[item.status] ?? 'neutral'}>
                {t(`penalties.statuses.${item.status}`)}
              </StatusPill>
              {/*
                Dwa uzasadnienia, dwa autorzy: sprzeciw to słowa pracownika,
                decyzja — pracodawcy. Jedno nie nadpisuje drugiego (grupa 42).
              */}
              {item.objectionComment !== null || item.objectionDecisionComment !== null ? (
                <span className={styles.rowNote}>
                  {item.objectionComment !== null
                    ? t(item.objectionCommentLegacy === true ? 'penalties.legacyReason' : 'penalties.objectionReason', {
                        text: item.objectionComment,
                      })
                    : ''}
                  {item.objectionComment !== null && item.objectionDecisionComment !== null
                    ? ' · '
                    : ''}
                  {item.objectionDecisionComment !== null
                    ? t('penalties.decisionReason', { text: item.objectionDecisionComment })
                    : ''}
                </span>
              ) : null}
              {canEdit && item.notifiedOn === null ? (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setError(null)
                    setNotifiedOn('')
                    setNotify(item)
                  }}
                >
                  {t('penalties.markNotified')}
                </Button>
              ) : null}
              {canEdit && item.status === 'sprzeciw' ? (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setError(null)
                    setComment('')
                    setDecide(item)
                  }}
                >
                  {t('penalties.decide')}
                </Button>
              ) : null}
              {canEdit && item.status !== 'niebyla' ? (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setError(null)
                    setVoidBasis('')
                    setVoidReason('')
                    setVoidTarget(item)
                  }}
                >
                  {t('penalties.void')}
                </Button>
              ) : null}
            </div>
          ))}
        </Card>
      )}

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title={t('penalties.formTitle')}>
        <p className={styles.modalLead}>{t('penalties.formLead')}</p>
        {error !== null ? (
          <InfoBanner tone="danger" className={styles.banner}>
            {t(error)}
            {issues.length > 0 ? (
              <ul>
                {issues.map((issue) => (
                  <li key={issue}>{t(`penalties.issues.${issue}`)}</li>
                ))}
              </ul>
            ) : null}
          </InfoBanner>
        ) : null}

        <div className={styles.formGrid}>
          <FormField label={t('penalties.fields.kind')} htmlFor={ids.kind}>
            <Select
              id={ids.kind}
              value={form.kind}
              onChange={(event) =>
                setForm({ ...form, kind: event.target.value as PenaltyKind | '' })
              }
            >
              <option value="">{t('penalties.fields.kindPlaceholder')}</option>
              {PENALTY_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {t(`penalties.kinds.${kind}`)}
                </option>
              ))}
            </Select>
          </FormField>
          {/*
            Katalog naruszeń jest ustawowy: kara pieniężna wchodzi tylko za
            cztery z nich (art. 108 § 2), a serwer tego pilnuje.
          */}
          <FormField
            label={t('penalties.fields.offence')}
            htmlFor={ids.offence}
            hint={t('penalties.fields.offenceHint')}
          >
            <Select
              id={ids.offence}
              value={form.offence}
              onChange={(event) => setForm({ ...form, offence: event.target.value })}
            >
              <option value="">{t('penalties.fields.offencePlaceholder')}</option>
              {PENALTY_OFFENCES.map((offence) => (
                <option key={offence} value={offence}>
                  {t(`penalties.offences.${offence}`)}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField label={t('penalties.fields.description')} htmlFor={ids.description}>
            <Input
              id={ids.description}
              value={form.description}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
            />
          </FormField>
          <FormField label={t('penalties.fields.offenceOn')} htmlFor={ids.offenceOn}>
            <Input
              id={ids.offenceOn}
              type="date"
              value={form.offenceOn}
              onChange={(event) => setForm({ ...form, offenceOn: event.target.value })}
            />
          </FormField>
          <FormField
            label={t('penalties.fields.learnedOn')}
            htmlFor={ids.learnedOn}
            hint={t('penalties.fields.learnedOnHint')}
          >
            <Input
              id={ids.learnedOn}
              type="date"
              value={form.learnedOn}
              onChange={(event) => setForm({ ...form, learnedOn: event.target.value })}
            />
          </FormField>
          <FormField
            label={t('penalties.fields.hearingOn')}
            htmlFor={ids.hearingOn}
            hint={t('penalties.fields.hearingOnHint')}
          >
            <Input
              id={ids.hearingOn}
              type="date"
              value={form.hearingOn}
              onChange={(event) => setForm({ ...form, hearingOn: event.target.value })}
            />
          </FormField>
          <FormField label={t('penalties.fields.appliedOn')} htmlFor={ids.appliedOn}>
            <Input
              id={ids.appliedOn}
              type="date"
              value={form.appliedOn}
              onChange={(event) => setForm({ ...form, appliedOn: event.target.value })}
            />
          </FormField>
          {form.kind === 'kara_pieniezna' ? (
            <FormField
              label={t('penalties.fields.amount')}
              htmlFor={ids.amount}
              hint={t('penalties.fields.amountHint')}
            >
              <Input
                id={ids.amount}
                inputMode="decimal"
                value={form.amount}
                onChange={(event) => setForm({ ...form, amount: event.target.value })}
              />
            </FormField>
          ) : null}
        </div>

        <div className={styles.actions}>
          <Button variant="secondary" onClick={() => setAddOpen(false)}>
            {t('penalties.cancel')}
          </Button>
          <Button disabled={!ready || add.isPending} onClick={() => add.mutate()}>
            {add.isPending ? <Spinner label={t('penalties.saving')} /> : t('penalties.submit')}
          </Button>
        </div>
      </Modal>

      <Modal
        open={notify !== null}
        onClose={() => setNotify(null)}
        title={t('penalties.notifyTitle')}
      >
        <p className={styles.modalLead}>{t('penalties.notifyLead')}</p>
        {error !== null ? (
          <InfoBanner tone="danger" className={styles.banner}>
            {t(error)}
          </InfoBanner>
        ) : null}
        <div className={styles.formGrid}>
          <FormField label={t('penalties.fields.notifiedOn')} htmlFor={ids.notifiedOn}>
            <Input
              id={ids.notifiedOn}
              type="date"
              value={notifiedOn}
              onChange={(event) => setNotifiedOn(event.target.value)}
            />
          </FormField>
        </div>
        <div className={styles.actions}>
          <Button variant="secondary" onClick={() => setNotify(null)}>
            {t('penalties.cancel')}
          </Button>
          <Button
            disabled={notifiedOn === '' || markNotified.isPending}
            onClick={() => markNotified.mutate()}
          >
            {t('penalties.submit')}
          </Button>
        </div>
      </Modal>

      <Modal
        open={decide !== null}
        onClose={() => setDecide(null)}
        title={t('penalties.decideTitle')}
      >
        <p className={styles.modalLead}>{t('penalties.decideLead')}</p>
        {decide?.objectionComment ? (
          <p className={styles.note}>{decide.objectionComment}</p>
        ) : null}
        {error !== null ? (
          <InfoBanner tone="danger" className={styles.banner}>
            {t(error)}
          </InfoBanner>
        ) : null}
        <div className={styles.formGrid}>
          <FormField label={t('penalties.fields.comment')} htmlFor={ids.comment}>
            <Input
              id={ids.comment}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
            />
          </FormField>
        </div>
        <div className={styles.actions}>
          <Button variant="secondary" onClick={() => setDecide(null)}>
            {t('penalties.cancel')}
          </Button>
          <Button
            variant="secondary"
            disabled={decision.isPending}
            onClick={() => decision.mutate(false)}
          >
            {t('penalties.reject')}
          </Button>
          <Button disabled={decision.isPending} onClick={() => decision.mutate(true)}>
            {t('penalties.accept')}
          </Button>
        </div>
      </Modal>

      <Modal
        open={voidTarget !== null}
        onClose={() => setVoidTarget(null)}
        title={t('penalties.voidTitle')}
      >
        <p className={styles.modalLead}>{t('penalties.voidLead')}</p>
        {error !== null ? (
          <InfoBanner tone="danger" className={styles.banner}>
            {t(error)}
          </InfoBanner>
        ) : null}
        <div className={styles.formGrid}>
          <FormField
            label={t('penalties.fields.voidBasis')}
            htmlFor={ids.voidBasis}
            hint={
              voidTarget?.voidOn
                ? t('penalties.fields.voidBasisHint', {
                    date: formatDate(voidTarget.voidOn, 'dot'),
                  })
                : undefined
            }
          >
            <Select
              id={ids.voidBasis}
              value={voidBasis}
              onChange={(event) => setVoidBasis(event.target.value as PenaltyVoidBasis | '')}
            >
              <option value="">{t('penalties.fields.voidBasisPlaceholder')}</option>
              {voidBases.map((basis) => (
                <option key={basis} value={basis}>
                  {t(`penalties.voidBases.${basis}`)}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField label={t('penalties.fields.voidReason')} htmlFor={ids.voidReason}>
            <Input
              id={ids.voidReason}
              value={voidReason}
              onChange={(event) => setVoidReason(event.target.value)}
            />
          </FormField>
        </div>
        {voidBasis !== '' ? (
          <InfoBanner tone="warning" className={styles.banner}>
            {t('penalties.voidEffects')}{' '}
            {refundGr > 0
              ? t('penalties.voidRefund', { amount: formatPLN(refundGr) })
              : voidTarget?.kind === 'kara_pieniezna' && voidTarget.deductedGr > 0
                ? t('penalties.voidNoRefund')
                : ''}
          </InfoBanner>
        ) : null}
        <div className={styles.actions}>
          <Button variant="secondary" onClick={() => setVoidTarget(null)}>
            {t('penalties.cancel')}
          </Button>
          <Button
            variant="danger"
            disabled={voidBasis === '' || voidPenalty.isPending}
            onClick={() => voidPenalty.mutate()}
          >
            {t('penalties.voidConfirm')}
          </Button>
        </div>
      </Modal>
    </section>
  )
}
